import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl';
import _ from 'lodash';

import {
  Button,
  IconButton,
  FormHelperText,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Tooltip,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import EditIcon from '@material-ui/icons/Edit';
import * as actions from '../../../../state';
import { dateToStr } from '../../../../utils';
import styles from '../styles.module.css';
import DataSourceAlertControls from './DataSourceAlertControls';
import DataSourceAlerts from './DataSourceAlerts';
import DataSourceColumns from './DataSourceColumns';
import DataSourceOptions from './DataSourceOptions';
import TimeRange from './common-controls/TimeRange';
import createChartNameFromSources from '../../../../utils/createChartNameFromSources';

const messages = defineMessages({
  ADD_DATA: {
    id: 'ADD_DATA',
    defaultMessage: 'Add Data',
  },
  CUSTOM_DATA: {
    id: 'CUSTOM_DATA',
    defaultMessage: 'Custom Data',
  },
  EDIT_SOURCE_TOOLTIP: {
    id: 'EDIT_SOURCE_TOOLTIP',
    defaultMessage: 'Edit data configuration',
  },
  DELETE_SOURCE_TOOLTIP: {
    id: 'DELETE_SOURCE_TOOLTIP',
    defaultMessage: 'Remove data',
  },
  NOTE_CONTENT: {
    id: 'NOTE_CONTENT',
    defaultMessage: 'Note Content',
  },
  NOTE_PLACEHOLDER: {
    id: 'NOTE_PLACEHOLDER',
    defaultMessage: 'What should this note say?',
  },
  IFRAME_CONTENT: {
    id: 'IFRAME_CONTENT',
    defaultMessage: 'Iframe URL',
  },
  IFRAME_PLACEHOLDER: {
    id: 'IFRAME_PLACEHOLDER',
    defaultMessage: 'http://example.com',
  },
  IFRAME_URL_HELPTEXT: {
    id: 'IFRAME_URL_HELPTEXT',
    // eslint-disable-next-line max-len
    defaultMessage: 'The URL must either be hosted on the same domain as this application or must support Cross-Origin Resource Sharing.',
  },
  GAUGE_LIMIT: {
    id: 'GAUGE_LIMIT',
    defaultMessage: 'Gauge upper limit',
  },
  MINUTES: {
    id: 'MINUTES',
    defaultMessage: 'Minutes',
  },
  SECONDS: {
    id: 'SECONDS',
    defaultMessage: 'Seconds',
  },
  AGENTS: {
    id: 'AGENTS',
    defaultMessage: 'Agents',
  },
  CALLS: {
    id: 'CALLS',
    defaultMessage: 'Calls',
  },
  PERCENT: {
    id: 'PERCENT',
    defaultMessage: 'Percent',
  },
});

function DataSources({
  alertConfig,
  intl,
  saveOrigAlert,
  saveOrigColumns,
  saveOrigSource,
  selectAlert,
  selectedChartType,
  setTabContent,
  tabContent,
}) {
  const dispatch = useDispatch();

  const chartsManifest = useSelector(actions.readChartsManifest);
  const selectedChart = useSelector(actions.readStateSelectedChart);

  const [selectedSource, setSelectedSource] = useState(null);

  const handleIframeChange = (event) => {
    const clonedChart = _.cloneDeep(selectedChart);
    if (clonedChart.data_sources.length) { // update existing source
      clonedChart.data_sources[0].queryParams = { url: event.target.value };
    } else {
      const newSource = { // update existing source
        index: 1, // put the index into the source, so we can reference it directly later
        type: 'url',
        stat: 'custom',
        queryParams: { url: event.target.value },
      };

      clonedChart.data_sources.push(newSource);
    }

    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const handleNoteChange = (event) => {
    const clonedChart = _.cloneDeep(selectedChart);
    const noteText = event.target.value;

    if (!clonedChart.data_sources || !clonedChart.data_sources.length) {
      clonedChart.data_sources = [{}];
    }

    clonedChart.data_sources[0].note_text = noteText;
    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const updateChartDataRolling = (timeRange) => {
    const selectedRange = timeRange.range;

    let timePeriod = 'dayAndHour';

    if (selectedRange.includes('hour')) {
      timePeriod = 'dayAndHourAndMinute';
    } else if (selectedRange.includes('week')) {
      timePeriod = 'day';
    } else if (selectedRange.includes('month')) {
      timePeriod = 'day';
    } else if (selectedRange.includes('year')) {
      timePeriod = 'month';
    }

    const clonedChart = _.cloneDeep(selectedChart);
    clonedChart.data_sources.forEach((source) => {
      source.queryParams.time_range = selectedRange; // eslint-disable-line no-param-reassign
      source.queryParams.period = timePeriod; // eslint-disable-line no-param-reassign
    });

    clonedChart.renderRequired = true;
    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const updateChartDataStatic = (timeRange) => {
    const endDate = new Date(timeRange.end_date); // eslint-disable-line camelcase
    const startDate = new Date(timeRange.start_date); // eslint-disable-line camelcase
    const timeDiff = endDate.getTime() - startDate.getTime();
    let timePeriod;
    if (timeDiff < 1000 * 60 * 60 * 2) { // less than 2 hours
      timePeriod = 'dayAndHourAndMinute';
    } else if (timeDiff < 1000 * 60 * 60 * 24 * 5) { // less than 5 days
      timePeriod = 'dayAndHour';
    } else if (timeDiff < 1000 * 60 * 60 * 24 * 7 * 4) { // less than 1 month
      timePeriod = 'day';
    } else if (timeDiff < 1000 * 60 * 60 * 24 * 7 * 52) { // less than 1 year
      timePeriod = 'month';
    }

    const clonedChart = _.cloneDeep(selectedChart);
    clonedChart.data_sources.forEach((source) => {
      source.queryParams.time_range = timeRange; // eslint-disable-line no-param-reassign
      source.queryParams.period = timePeriod; // eslint-disable-line no-param-reassign
    });

    clonedChart.renderRequired = true;
    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const updateChartDataDaily = (timeRange) => {
    const clonedChart = _.cloneDeep(selectedChart);

    clonedChart.data_sources.forEach((source) => {
      source.queryParams.time_range = timeRange; // eslint-disable-line no-param-reassign
      source.queryParams.period = 'dayAndHour'; // eslint-disable-line no-param-reassign
    });

    clonedChart.renderRequired = true;
    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const handleRangeTypeSelect = (event) => {
    const timeRangeType = event.target.value;

    switch (timeRangeType) {
      case 'rolling': // set default range to '1_day'
        updateChartDataRolling({
          type: timeRangeType,
          range: '1_day',
        });
        break;

      case 'static': // set default dates to yesterday and today
        updateChartDataStatic({
          type: timeRangeType,
          start_date: dateToStr(new Date(new Date().setDate(new Date().getDate() - 1))), // yesterday
          end_date: dateToStr(new Date()), // now
        });
        break;

      case 'daily':
        updateChartDataDaily({
          type: timeRangeType,
          daytimes: [
            {
              day: 0, active: false, start_time: { h: 9, m: 0 }, end_time: { h: 17, m: 0 },
            }, // sunday
            {
              day: 1, active: true, start_time: { h: 9, m: 0 }, end_time: { h: 17, m: 0 },
            },
            {
              day: 2, active: true, start_time: { h: 9, m: 0 }, end_time: { h: 17, m: 0 },
            },
            {
              day: 3, active: true, start_time: { h: 9, m: 0 }, end_time: { h: 17, m: 0 },
            },
            {
              day: 4, active: true, start_time: { h: 9, m: 0 }, end_time: { h: 17, m: 0 },
            },
            {
              day: 5, active: true, start_time: { h: 9, m: 0 }, end_time: { h: 17, m: 0 },
            },
            {
              day: 6, active: false, start_time: { h: 9, m: 0 }, end_time: { h: 17, m: 0 },
            }, // saturday
          ],
        });
        break;
      default:
    }
  };

  const handleRangeSelect = (event) => {
    updateChartDataRolling({
      type: 'rolling',
      range: event.target.value,
    });
  };

  const handleDateInput = (moment, dateParam) => {
    const clonedChart = _.cloneDeep(selectedChart);
    const timeRange = clonedChart.data_sources[0].queryParams.time_range;
    timeRange[dateParam] = dateToStr(new Date(moment.valueOf()));
    updateChartDataStatic(timeRange);
  };

  const handleDailyDayToggle = (dayInt) => {
    const clonedChart = _.cloneDeep(selectedChart);
    const timeRange = clonedChart.data_sources[0].queryParams.time_range;

    timeRange.daytimes.forEach((daytime) => {
      if (daytime.day === dayInt) {
        daytime.active = !daytime.active; // eslint-disable-line no-param-reassign
      }
    });

    updateChartDataDaily(timeRange);
  };

  const handleDailyTimeChange = (moment, timeParam) => {
    const clonedChart = _.cloneDeep(selectedChart);
    const timeRange = clonedChart.data_sources[0].queryParams.time_range;

    const newDatetime = new Date(moment.valueOf());
    const newHours = newDatetime.getHours() < 10 ? `0${newDatetime.getHours()}` : newDatetime.getHours();
    const newMins = newDatetime.getMinutes() < 10 ? `0${newDatetime.getMinutes()}` : newDatetime.getMinutes();

    timeRange.daytimes.forEach((daytime) => {
      daytime[timeParam].h = newHours; // eslint-disable-line no-param-reassign
      daytime[timeParam].m = newMins; // eslint-disable-line no-param-reassign
    });

    updateChartDataDaily(timeRange);
  };

  const selectNewSource = () => {
    setTabContent('selectStat');
  };

  const editSource = (source) => {
    setTabContent('configureSource');
    saveOrigSource(source);
    setSelectedSource(source);
  };

  const toggleColumns = () => {
    setTabContent('toggleColumns');
    const source = selectedChart.data_sources[0];
    saveOrigColumns(source.queryParams.stats);
    setSelectedSource(source);
  };

  const addNewAlert = () => {
    setTabContent('configureAlert');

    const source = selectedChart.data_sources[0];
    setSelectedSource(source);

    const statDef = chartsManifest[selectedChartType].dataOptions[source.stat];

    let alertDirection = 'above'; // default direction is above
    if (source && source.alerts && source.alerts.length) { // if previous alerts exist
      if (statDef.hasColumns) { // if this is a table
        const alertMatch = source.alerts.find((alert) => alert.column === alertConfig.column); // look for alerts matching this column
        if (alertMatch) { alertDirection = alertMatch.direction; }
      } else { // otherwise
        alertDirection = source.alerts[0].direction; // set direction base off first alert
      }
    }

    selectAlert({
      threshold: null,
      sourceIndex: (selectedSource && selectedSource.index) || 0,
      direction: alertDirection,
      color: '#ff0000',
    });
  };

  const editAlert = (selectedAlert, aId) => {
    setTabContent('configureAlert');
    saveOrigAlert(selectedAlert);
    selectedAlert.index = aId; // eslint-disable-line no-param-reassign
    selectAlert(selectedAlert);
  };

  const deleteSource = (indexToDelete) => {
    const clonedChart = _.cloneDeep(selectedChart);
    clonedChart.data_sources.splice(indexToDelete, 1);
    clonedChart.name = createChartNameFromSources(chartsManifest, clonedChart);
    dispatch(actions.updateSelectedChart(clonedChart));
  };

  const renderSource = (source, sId, isEditable) => {
    let statDef = chartsManifest[selectedChartType].dataOptions[source.stat];
    const secondaryLabelChunks = [];

    if (source.stat === 'custom') {
      statDef = {
        categoryDefs: false,
        hasColumns: false,
        label: intl.formatMessage({ ...messages.CUSTOM_DATA }),
      };

      secondaryLabelChunks.push(<span>{source.queryParams.url}</span>);
    }

    if (statDef.hasCategories) {
      const categoryLabel = statDef.categoryDefs.find((categoryDef) => categoryDef.category === source.category).label;
      const unitLabel = intl.formatMessage({ ...messages[statDef.unit.toUpperCase()] });
      secondaryLabelChunks.push(
        <div>
          {categoryLabel}
          {' '}
          {unitLabel}
        </div>,
      );
    }

    if (statDef.hasColumns) {
      secondaryLabelChunks.push(<span>{source.queryParams.stats.join(', ')}</span>);
    }

    if (source.queryParams.outOf) {
      secondaryLabelChunks.push(
        <span>{`${intl.formatMessage({ ...messages.GAUGE_LIMIT })}: ${source.queryParams.outOf}`}</span>,
      );
    }

    if (source.filters && source.filters.length) {
      const filterStrs = source.filters
        .map((filter) => (filter.name ? `${filter.name} ${filter.desc}` : filter.value))
        .join(', ');
      secondaryLabelChunks.push(<span title={filterStrs}>{` [${filterStrs}]`}</span>);
    }

    return (
      <ListItem key={sId} className={styles.sourceListItem}>
        <ListItemText
          disableTypography
          primary={<h3>{statDef.label}</h3>}
          secondary={<div className={styles.secondaryLabelA}>{secondaryLabelChunks}</div>}
          className={styles.switchTitle}
          classes={{ secondary: styles.secondaryLabel }}
        />

        <ListItemSecondaryAction className={styles.deleteAction}>
          { isEditable
            ? (
              <Tooltip disableFocusListener title={<FormattedMessage {...messages.EDIT_SOURCE_TOOLTIP} />}>
                <IconButton onClick={() => editSource(source)} className={styles.sourceActionBtn}>
                  <EditIcon style={{ width: 16, height: 16 }} />
                </IconButton>
              </Tooltip>
            )
            : ''}

          <Tooltip disableFocusListener title={<FormattedMessage {...messages.DELETE_SOURCE_TOOLTIP} />}>
            <IconButton onClick={() => deleteSource(sId)} className={styles.sourceActionBtn}>
              <DeleteIcon style={{ width: 16, height: 16 }} />
            </IconButton>
          </Tooltip>
        </ListItemSecondaryAction>
      </ListItem>
    );
  };

  const chartDefinition = chartsManifest[selectedChartType];
  const chartHasSource = selectedChart.data_sources && selectedChart.data_sources.length;
  const hasCustomSource = chartHasSource && selectedChart.data_sources[0].stat === 'custom';
  const hasLiveSource = chartHasSource
    && !hasCustomSource
    && chartDefinition
    && chartDefinition.dataOptions[selectedChart.data_sources[0].stat].liveData;

  if (selectedChart.type === 'ResponsiveNote') {
    const noteText = chartHasSource ? selectedChart.data_sources[0].note_text : '';

    return (
      <div style={{ padding: 12 }}>
        <TextField
          value={noteText}
          className={styles.noteTextArea}
          label={intl.formatMessage({ ...messages.NOTE_CONTENT })}
          placeholder={intl.formatMessage({ ...messages.NOTE_PLACEHOLDER })}
          onChange={handleNoteChange}
          multiline
          variant="outlined"
          rowsMax={25}
          rows={10}
        />
      </div>
    );
  }

  if (selectedChart.type === 'ResponsiveIFrame') {
    const iframeUrl = chartHasSource ? selectedChart.data_sources[0].queryParams.url : '';

    return (
      <div style={{ padding: 12 }}>
        <TextField
          value={iframeUrl}
          className={styles.noteTextArea}
          label={intl.formatMessage({ ...messages.IFRAME_CONTENT })}
          placeholder={intl.formatMessage({ ...messages.IFRAME_PLACEHOLDER })}
          onChange={handleIframeChange}
          variant="outlined"
          rowsMax={25}
          rows={10}
        />

        <FormHelperText>
          <FormattedMessage {...messages.IFRAME_URL_HELPTEXT} />
        </FormHelperText>
      </div>
    );
  }

  // if adding/editing a source, show controls
  if (['selectStat', 'configureSource', 'toggleColumns'].includes(tabContent)) {
    return (
      <DataSourceOptions
        selectedChartType={selectedChartType}
        selectedSource={selectedSource}
        setTabContent={setTabContent}
        tabContent={tabContent}
        updateSelectedSource={setSelectedSource}
      />
    );
  }

  if (tabContent === 'configureAlert') {
    return (
      <DataSourceAlertControls
        alertConfig={alertConfig}
        selectAlert={selectAlert}
      />
    );
  }

  return (
    <div className={`${styles.list} ${styles.dataTabContent}`}>
      <Button
        color="primary"
        onClick={selectNewSource}
        className={styles.addSourceBtns}
        disabled={Boolean(chartHasSource && !chartDefinition.multipleSourceSupport)}
      >
        <AddIcon />
        {' '}
        <FormattedMessage {...messages.ADD_DATA} />
      </Button>

      { selectedChart && chartHasSource // if there are data sources, list them
        ? (
          <List dense className={`${styles.menu} ${styles.sourcesMenu}`}>
            { selectedChart.data_sources.map((source, sId) => renderSource(source, sId, true)) }
          </List>
        )
        : ''}

      {(
        chartDefinition.usesTimeRange
        && chartHasSource
        && !hasCustomSource
        && !hasLiveSource
        && selectedChart.data_sources[0].stat !== 'Queued'
      )
        ? (
          <TimeRange
            timeRange={selectedChart.data_sources[0].queryParams.time_range}
            handleRangeTypeSelect={handleRangeTypeSelect}
            handleRangeSelect={handleRangeSelect}
            handleDateInput={handleDateInput}
            handleDailyDayToggle={handleDailyDayToggle}
            handleDailyTimeChange={handleDailyTimeChange}
          />
        )
        : ''}

      { chartDefinition.usesColumns && chartHasSource && !hasCustomSource
        ? (
          <DataSourceColumns
            toggleColumns={toggleColumns}
          />
        )
        : ''}

      { chartDefinition.alertSupport && !(hasCustomSource && selectedChart.type === 'ResponsiveTable')
        ? (
          <DataSourceAlerts
            editAlert={editAlert}
            addNewAlert={addNewAlert}
            selectAlert={selectAlert}
            chartHasSource={chartHasSource}
          />
        )
        : ''}
    </div>
  );
}

DataSources.propTypes = {
  alertConfig: PropTypes.shape({}).isRequired,
  intl: PropTypes.shape({}).isRequired,
  saveOrigAlert: PropTypes.func.isRequired,
  saveOrigColumns: PropTypes.func.isRequired,
  saveOrigSource: PropTypes.func.isRequired,
  selectAlert: PropTypes.func.isRequired,
  selectedChartType: PropTypes.string.isRequired,
  setTabContent: PropTypes.func.isRequired,
  tabContent: PropTypes.string.isRequired,
};

export default injectIntl(DataSources);
